import { useTranslation } from "react-i18next";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import type {
  PreflightReportAsset,
  PreflightReportDetail,
} from "../../types/preflight.types";
import { exportAssetToJson, exportAssetToText } from "../../lib/export-report";
import { exportAssetToXlsx } from "../../lib/report-workbook";

export interface AssetExportMenuProps {
  /** Parent report — supplies the report-level context for the per-asset file. */
  report: PreflightReportDetail;
  asset: PreflightReportAsset;
  className?: string;
}

/** Per-asset export (Export item 2): downloads just this creative's report. */
export function AssetExportMenu({
  report,
  asset,
  className,
}: AssetExportMenuProps) {
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <TooltipProvider delayDuration={250} skipDelayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className={cn("text-muted-foreground shrink-0", className)}
                aria-label={t("preflight.export.assetAria")}
              >
                <Icon name="download" size="sm" color="inherit" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">
            {t("preflight.export.assetTrigger")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => exportAssetToXlsx(report, asset)}>
          {t("preflight.export.formatExcel")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => exportAssetToJson(report, asset)}>
          {t("preflight.export.formatJson")}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => exportAssetToText(report, asset)}>
          {t("preflight.export.formatText")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
